/**
 * Painel Encaminhar chamado (áreas + técnicos) — modal V2.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Forward, X } from 'lucide-react';
import { ENCAMINHAR_SESSOES } from '../utils/orderDetailsEncaminharUtils';
import './OrderDetailsEncaminharChamadoModal.css';

const CLOSE_MS = 280;

export function OrderDetailsEncaminharChamadoModal({
  open,
  onClose,
  orderId,
  areas = [],
  onConfirm,
}) {
  const [mounted, setMounted] = useState(open);
  const [filter, setFilter] = useState('');
  const [selectedSessaoId, setSelectedSessaoId] = useState('previsto-hoje');
  const [selectedTech, setSelectedTech] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useEffect(() => {
    if (!open && mounted) {
      const t = setTimeout(() => setMounted(false), CLOSE_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (!open) {
      setFilter('');
      setSelectedSessaoId('previsto-hoje');
      setSelectedTech(null);
      setSubmitting(false);
    }
  }, [open]);

  const filteredAreas = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return areas;
    return areas
      .map((area) => {
        const areaName = String(area.name || '').toLowerCase();
        const techs = areaName.includes(q)
          ? area.technicians
          : (area.technicians || []).filter((t) => String(t).toLowerCase().includes(q));
        if (!techs?.length) return null;
        return { ...area, technicians: techs };
      })
      .filter(Boolean);
  }, [areas, filter]);

  const handleConfirm = async () => {
    if (!selectedTech || !selectedSessaoId || submitting) return;
    const sessao = ENCAMINHAR_SESSOES.find((s) => s.id === selectedSessaoId);
    if (!sessao) return;
    setSubmitting(true);
    try {
      await onConfirm?.({
        technicianName: selectedTech,
        sessaoId: selectedSessaoId,
        groupName: sessao.groupName,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) return null;

  return (
    <div
      className={`odv2-encaminhar-overlay${open ? ' odv2-encaminhar-overlay--open' : ''}`}
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className={`odv2-encaminhar-panel${open ? ' odv2-encaminhar-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="odv2-encaminhar-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="odv2-encaminhar-header">
          <h2 id="odv2-encaminhar-title" className="odv2-encaminhar-title">
            <Forward size={18} strokeWidth={2} aria-hidden />
            Encaminhar chamado
            {orderId ? <span className="odv2-encaminhar-os">OS {orderId}</span> : null}
          </h2>
          <button
            type="button"
            className="odv2-encaminhar-close"
            aria-label="Fechar"
            disabled={submitting}
            onClick={onClose}
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </header>

        <div className="odv2-encaminhar-body">
          <p className="odv2-encaminhar-label">Sessão de destino</p>
          <div className="odv2-encaminhar-sessoes" role="group" aria-label="Sessão de destino">
            {ENCAMINHAR_SESSOES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`odv2-encaminhar-sessao-btn${selectedSessaoId === s.id ? ' odv2-encaminhar-sessao-btn--active' : ''}`}
                onClick={() => setSelectedSessaoId(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <p className="odv2-encaminhar-label">Selecione o técnico</p>
          <input
            type="text"
            className="odv2-encaminhar-filter"
            placeholder="Filtrar por área ou técnico…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filtrar técnicos"
          />

          {filteredAreas.length === 0 ? (
            <p className="odv2-encaminhar-empty">Nenhuma área ou técnico encontrado.</p>
          ) : (
            <div className="odv2-encaminhar-tech-list">
              {filteredAreas.map((area) => (
                <div key={area.id} className="odv2-encaminhar-area">
                  <span className="odv2-encaminhar-area-name">{area.name}</span>
                  <div className="odv2-encaminhar-techs">
                    {(area.technicians || []).map((techName) => {
                      const isSelected = selectedTech === techName;
                      return (
                        <button
                          key={`${area.id}-${techName}`}
                          type="button"
                          className={`odv2-encaminhar-tech-btn${isSelected ? ' odv2-encaminhar-tech-btn--selected' : ''}`}
                          onClick={() => setSelectedTech(techName)}
                        >
                          {techName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="odv2-encaminhar-actions">
          <button
            type="button"
            className="odv2-encaminhar-cancel-btn"
            disabled={submitting}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="odv2-encaminhar-confirm-btn"
            disabled={submitting || !selectedTech}
            onClick={handleConfirm}
          >
            {submitting ? 'Encaminhando…' : 'Confirmar'}
          </button>
        </footer>
      </div>
    </div>
  );
}
